"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useId, useState } from "react";
import { StratosRichText } from "../StratosRichText/StratosRichText";
import type { IAccordionListProps } from "./AccordionList.props";

export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div
            className={cn(
              "overflow-hidden rounded-[20px] border border-horizon-blue/70 transition-colors duration-300",
              open ? "bg-mist" : "bg-cloud-white",
            )}
            key={index}
          >
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className="flex min-h-20 w-full cursor-pointer items-center justify-between gap-5 px-5 py-5 text-left font-stratos-display text-xl font-bold transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:px-7 md:text-2xl"
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span>{item.title}</span>
                <Icon
                  className={cn(
                    "size-5 shrink-0 transition-transform duration-200",
                    open && "rotate-45",
                  )}
                  name="plus"
                />
              </button>
            </h3>
            <div
              aria-hidden={!open}
              aria-labelledby={triggerId}
              className={cn(
                "grid transition-[grid-template-rows] duration-300 ease-out",
                open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
              id={panelId}
              inert={!open}
              role="region"
            >
              <div className="overflow-hidden">
                <StratosRichText
                  className="max-w-[65ch] px-5 pb-7 text-base text-steel md:px-7 md:text-lg"
                  content={item.content}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionList.displayName = "Content::AccordionList";
